import { Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import VehicleClusterReinstallHeroCard from './VehicleClusterReinstallHeroCard'
import {
  NeedsClusterReinstalledClusterWithOdometerPhotoDto,
  NeedsClusterReinstalledComboPhotoDto,
  NeedsClusterReinstalledDetailsDto,
  NeedsClusterReinstalledImporterDoorLabelPhotoDto,
  VehicleOdometerAfterModificationPhotoDto,
  VehicleCheckInCurrentDto,
  VehicleCheckInDetailsDto
} from 'dtos'
import { enqueueSnackbar } from 'notistack'
import Step1OdometerReadingDetails from './Step1OdometerReadingDetails'
import Step2ClusterReinstallPhotos from './Step2ClusterReinstallPhotos'
import Step3ClusterReinstallReview from './Step3ClusterReinstallReview'
import { ArrowBack } from '@mui/icons-material'

export interface VehicleClusterReinstallPhotosProps {
  clusterWithOdometerPhoto: NeedsClusterReinstalledClusterWithOdometerPhotoDto
  setClusterWithOdometerPhoto: React.Dispatch<React.SetStateAction<NeedsClusterReinstalledClusterWithOdometerPhotoDto>>
  importerDoorLabelPhoto: NeedsClusterReinstalledImporterDoorLabelPhotoDto
  setImporterDoorLabelPhoto: React.Dispatch<React.SetStateAction<NeedsClusterReinstalledImporterDoorLabelPhotoDto>>
  comboPhoto: NeedsClusterReinstalledComboPhotoDto
  setComboPhoto: React.Dispatch<React.SetStateAction<NeedsClusterReinstalledComboPhotoDto>>
  odometerAfterModificationPhoto: VehicleOdometerAfterModificationPhotoDto
  setOdometerAfterModificationPhoto: React.Dispatch<React.SetStateAction<VehicleOdometerAfterModificationPhotoDto>>
}

export default function VehicleClusterReinstall() {
  const { vehicleGuid } = useParams()
  const layout = useLayout()
  const navigate = useNavigate()

  const [initialValues, setInitialValues] = useState<NeedsClusterReinstalledDetailsDto>(new NeedsClusterReinstalledDetailsDto())

  const [checkInValues, setCheckInValues] = useState<VehicleCheckInCurrentDto>(new VehicleCheckInCurrentDto())

  useEffect(() => {
    if (vehicleGuid) {
      layout.setIsLayoutLoading(true)
      Promise.all([
        controllers.getNeedsClusterReinstalledDetails(vehicleGuid),
        controllers.getVehicleCheckInCurrent(vehicleGuid),
        controllers.getNeedsClusterReinstalledClusterWithOdometerLabelPhotoInformation(vehicleGuid),
        controllers.getNeedsClusterReinstalledImporterDoorLabelPhotoInformation(vehicleGuid),
        controllers.getNeedsClusterReinstalledComboLabelPhotoInformation(vehicleGuid),
        controllers.getVehicleOdometerAfterModificationPhoto(vehicleGuid)
      ])
        .then(
          ([
            clusterReinstallDetailsResponse,
            checkInCurrentResponse,
            clusterWithOdometerResponse,
            importerDoorLabelResponse,
            comboPhotoResponse,
            odometerAfterModificationResponse
          ]) => {
            setInitialValues(clusterReinstallDetailsResponse.value)
            setCheckInValues(checkInCurrentResponse.value)
            setClusterWithOdometerPhoto(clusterWithOdometerResponse.value)
            setImporterDoorLabelPhoto(importerDoorLabelResponse.value)
            setComboPhoto(comboPhotoResponse.value)
            setOdometerAfterModificationPhoto(odometerAfterModificationResponse.value)
          }
        )
        .finally(() => {
          layout.setIsLayoutLoading(false)
        })
    }
  }, [vehicleGuid])

  const [activeStep, setActiveStep] = useState<number>(0)

  const [importerDoorLabelPhoto, setImporterDoorLabelPhoto] = useState<NeedsClusterReinstalledImporterDoorLabelPhotoDto>(
    new NeedsClusterReinstalledImporterDoorLabelPhotoDto()
  )
  const [comboPhoto, setComboPhoto] = useState<NeedsClusterReinstalledComboPhotoDto>(new NeedsClusterReinstalledComboPhotoDto())
  const [clusterWithOdometerPhoto, setClusterWithOdometerPhoto] = useState<NeedsClusterReinstalledClusterWithOdometerPhotoDto>(
    new NeedsClusterReinstalledClusterWithOdometerPhotoDto()
  )
  const [odometerAfterModificationPhoto, setOdometerAfterModificationPhoto] =
    useState<VehicleOdometerAfterModificationPhotoDto>(new VehicleOdometerAfterModificationPhotoDto())

  const onBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1))
  }

  const onNext = () => {
    setActiveStep(prev => Math.min(3, prev + 1))
  }

  return (
    <Grid container spacing={1} alignItems='center'>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container justifyContent='space-between'>
              <Grid item>
                <Typography fontWeight='bold' variant='h5' sx={{ pr: 1 }}>
                  Needs Cluster Reinstalled
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={2} alignItems='center' justifyContent='flex-end'>
                <Grid item>
                  <Button
                    onClick={() => {
                      navigate('/to-do-list')
                    }}
                    color='primary'
                    startIcon={<ArrowBack />}
                    variant='text'
                  >
                    BACK
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <VehicleClusterReinstallHeroCard vehicle={initialValues} />
      </Grid>

      {activeStep === 0 && (
        <Grid item xs={12}>
          <Step1OdometerReadingDetails
            initialValues={initialValues}
            checkInValues={{
              vehicleCheckInOdometerReadingMileage: checkInValues.odometerReadingMileage,
              vehicleCheckInOdometerReadingUnitOfMeasurement: checkInValues.odometerReadingUnitOfMeasurement,
              vehicleCheckInOdometerReadingMi: checkInValues.odometerReadingMi,
              vehicleCheckInOdometerReadingKm: checkInValues.odometerReadingKm
            }}
            vehicleGuid={vehicleGuid}
            onNext={values => {
              const { clusterReinstallOdometerReadingMiDifference } = values

              layout.setIsLayoutLoading(true)

              controllers
                .updateNeedsClusterReinstalledDetails(vehicleGuid!, values)
                .then(response => {
                  enqueueSnackbar('Current Odometer Reading Saved Successfully!', { variant: 'success' })
                  setInitialValues(response.value)
                  if (
                    clusterReinstallOdometerReadingMiDifference !== undefined &&
                    (clusterReinstallOdometerReadingMiDifference < 0 || clusterReinstallOdometerReadingMiDifference >= 100)
                  ) {
                    controllers
                      .sendMileageDiscrepancyEmail(vehicleGuid!)
                      .then(() => {
                        enqueueSnackbar('Mileage discrepancy detected. Mileage Discrepancy Email Sent.', { variant: 'warning' })
                        setTimeout(() => {
                          navigate('/to-do-list', { replace: true })
                        }, 2000)
                      })
                      .catch(error => {
                        enqueueSnackbar('Error sending mileage discrepancy email. Please try again.', { variant: 'error' })
                      })
                  } else {
                    onNext()
                  }
                })
                .catch(error => {
                  enqueueSnackbar('Error updating odometer reading. Please try again.', { variant: 'error' })
                })
                .finally(() => {
                  layout.setIsLayoutLoading(false)
                })
            }}
          />
        </Grid>
      )}

      {activeStep === 1 && (
        <Grid item xs={12}>
          <Step2ClusterReinstallPhotos
            onBack={onBack}
            onNext={onNext}
            vehicleOdometerReadingDetails={initialValues}
            vehicleGuid={vehicleGuid}
            importerDoorLabelPhoto={importerDoorLabelPhoto}
            setImporterDoorLabelPhoto={setImporterDoorLabelPhoto}
            comboPhoto={comboPhoto}
            setComboPhoto={setComboPhoto}
            clusterWithOdometerPhoto={clusterWithOdometerPhoto}
            setClusterWithOdometerPhoto={setClusterWithOdometerPhoto}
            odometerAfterModificationPhoto={odometerAfterModificationPhoto}
            setOdometerAfterModificationPhoto={setOdometerAfterModificationPhoto}
          />
        </Grid>
      )}

      {activeStep === 2 && (
        <Grid item xs={12}>
          <Step3ClusterReinstallReview
            onBack={onBack}
            onNext={() => {
              layout.setIsLayoutLoading(true)
              controllers
                .updateNeedsClusterReinstalledDetails(vehicleGuid!, {
                  ...initialValues
                })
                .then(response => {
                  enqueueSnackbar('Cluster Reinstallation Process Completed Successfully!', { variant: 'success' })
                  setInitialValues(response.value)
                  navigate('/to-do-list', { replace: true })
                })
                .finally(() => {
                  layout.setIsLayoutLoading(false)
                })
            }}
            vehicleOdometerReadingDetails={initialValues}
            vehicleGuid={vehicleGuid}
            importerDoorLabelPhoto={importerDoorLabelPhoto}
            setImporterDoorLabelPhoto={setImporterDoorLabelPhoto}
            comboPhoto={comboPhoto}
            setComboPhoto={setComboPhoto}
            clusterWithOdometerPhoto={clusterWithOdometerPhoto}
            setClusterWithOdometerPhoto={setClusterWithOdometerPhoto}
            odometerAfterModificationPhoto={odometerAfterModificationPhoto}
            setOdometerAfterModificationPhoto={setOdometerAfterModificationPhoto}
          />
        </Grid>
      )}
    </Grid>
  )
}
